import React, { useState } from 'react';
import Container from './shared/Container';
import useScrollFade from '../hooks/useScrollFade';

const faqItems = [
  {
    question: "How does Vermillion integrate with our existing systems?",
    answer: "Vermillion offers flexible integration options including API connections, secure data transfer protocols, and native integrations with common enterprise platforms. Our implementation team will assess your current systems and develop a tailored integration approach that minimizes disruption while maximizing data flow."
  },
  {
    question: "How quickly can we expect to see ROI?",
    answer: "Most clients begin seeing measurable ROI within the first 2–3 months after go-live. Initial gains come from processing time reduction and elimination of manual keying errors. Decision automation compounds this over time as more workflows are encoded and verified. Our customers typically achieve 4–8× ROI within the first year."
  },
  {
    question: "What makes Vermillion different from other AI platforms?",
    answer: "Most AI tools are probabilistic — they produce outputs that vary, can't be audited, and drift over time. Vermillion Evident converts your governing documents into deterministic programs: same input always produces the same output, every decision is traceable to a specific rule, and updates are verified by your own experts before going live. Vermillion Intake pairs this with extraction that routes uncertain fields for human review rather than guessing — so accuracy is guaranteed, not just claimed at deployment."
  },
  {
    question: "How does implementation work?",
    answer: "Implementation follows four phases: Scoping, Build & Integrate, Expert Verification, and Launch. We map your workflows and document types first, encode your governing documents and connect to your systems, have your team review and approve every rule, then go live with full support. Most clients are operational within 4–8 weeks, with initial value in as little as two weeks."
  }
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="py-6 border-b border-gray-200">
      <dt>
        <button
          type="button"
          className="text-left w-full flex justify-between items-start text-gray-900 focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="font-medium">{question}</span>
          <span className="ml-6 flex items-center shrink-0">
            <svg
              className={`h-6 w-6 origin-center text-vermillion-600 transition-transform duration-200${isOpen ? ' rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </button>
      </dt>
      <dd className={`mt-2${isOpen ? '' : ' hidden'}`}>
        <p className="text-[0.9375rem] leading-[1.7] text-gray-500">
          {answer}
        </p>
      </dd>
    </div>
  );
};

const FAQ = () => {
  const [ref, visible] = useScrollFade();

  return (
    <section id="faq" className="py-16 sm:py-24 bg-surface-secondary">
      <Container>
        <div ref={ref} className={`scroll-fade${visible ? ' visible' : ''}`}>
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">Frequently Asked Questions</h2>
            <p className="mt-4 text-[1.0625rem] leading-[1.7] text-gray-500 max-w-lg mx-auto">
              Everything you need to know before getting started.
            </p>
          </div>

          <div className="mt-12 max-w-3xl mx-auto border-t border-gray-200">
            {faqItems.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
              />
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default FAQ;
